import React, { useEffect, useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  Chip,
  Grid,
  Button,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Autocomplete,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useSelector } from "react-redux";
import ReusableDialog from "../../../../common/dialog/dialog";
import axios from "axios";

const NonPhysicalWitness = ({ updateFormData, nextStep, prevStep, formData }) => {
  const user = useSelector((state) => state.auth.user); // User data from Redux store
  const [users, setUsers] = useState([]);
  const [witnessData, setWitnessData] = useState([]);
  const [name, setName] = useState("");
  const [role, setRole] = useState("Involved");
  const [category, setCategory] = useState("Adult");
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    if (formData && formData.length > 0) {
      setWitnessData(formData);
    }
  }, [formData]);

  // Handle role selection (Involved/Witness)
  const handleRoleChange = (event, newRole) => {
    if (newRole !== null) {
      setRole(newRole);
    }
  };

  // Handle category selection (Adult/Child)
  const handleCategoryChange = (event, newCategory) => {
    if (newCategory !== null) {
      setCategory(newCategory);
    }
  };

  // Handle form submission - Add new witness to table
  const handleProceed = () => {
    if (name.trim() === "") {
      alert("Please enter a name.");
      return;
    }

    // Add new witness to the state list
    setWitnessData((prevData) => [...prevData, { name, role, category }]);

    // Reset form fields
    setName("");
    setRole("Involved");
    setCategory("Adult");

    // Close Dialog
    setDialogOpen(false);
  };

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        // Make the API request
        const response = await axios.get("/api/auth/users");
        // Filter users with roleid === 4 and update state
        const filteredUsers = response.data.filter((usera) => usera.id !== user?.id);
        setUsers(filteredUsers);
      } catch (error) {
        console.error("Error fetching tasks:", error);
      }
    };

    fetchUsers();
  }, [user?.id]);

  return (
    <Box sx={{ pl: 4, pr: 4, ml: 2 }}>
      <Chip
        variant='contained'
        sx={{
          fontSize: "14px",
          backgroundColor: "#F2F6FF",
          color: "blue",
        }}
        label='Daily Log'
        size='small'
      />
      <Typography sx={{ fontSize: "24px", color: "black", fontWeight: "bold" }}>Non - Physical Intervention</Typography>
      <Typography variant='subtitle2' sx={{ color: "#757575" }}>
        Date: {new Date().toLocaleDateString()} &nbsp; • &nbsp;{" "}
        <span style={{ color: "#1751D0" }}>{user.username}</span>
      </Typography>
      <Box sx={{ borderBottom: "1px solid #E0E0E0", mt: 2, mb: 2 }} />

      <Grid container spacing={2}>
        <Grid item lg={8}>
          <Typography sx={{ fontSize: "18px", fontWeight: "bold", color: "#333", mb: 2 }}>
            Witness to Intervention
          </Typography>

          {/* Table */}
          <TableContainer component={Paper} elevation={3}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#E3F2FD" }}>
                  <TableCell sx={{ fontWeight: "bold", color: "#1976D2" }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: "bold", color: "#1976D2" }}>Involved/Witness</TableCell>
                  <TableCell sx={{ fontWeight: "bold", color: "#1976D2" }}>Adult/Child</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {witnessData.map((witness, index) => (
                  <TableRow key={index}>
                    <TableCell>{witness.name}</TableCell>
                    <TableCell>{witness.role}</TableCell>
                    <TableCell sx={{ color: "#333" }}>{witness.category}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <br />
          <Button color='primary' onClick={() => setDialogOpen(true)}>
            <AddIcon /> Add Another Involved / Witness
          </Button>

          {/* Reusable Dialog for Adding Witness */}
          <ReusableDialog
            isOpen={dialogOpen}
            onClose={() => setDialogOpen(false)}
            title='Add Another Involved / Witness'
            onSubmit={handleProceed}
            submitText='Proceed'
            cancelText='Cancel'
          >
            {/* Description */}
            <Typography variant='body2' sx={{ color: "#757575", mb: 2 }}>
              Add the relevant details to the fields to add another person to the involved parties.
            </Typography>

            {/* Name Field */}
            <Typography sx={{ fontSize: "14px", mb: 1, color: "black" }}>Name</Typography>
            <Autocomplete
              id='name'
              options={users?.map((item) => item?.username)}
              value={name}
              onChange={(event, newValue) => {
                setName(newValue);
              }}
              renderInput={(params) => (
                <>
                  <TextField {...params} placeholder='Select' />
                </>
              )}
              fullWidth
            />
            <Grid container>
              <Grid item lg={10}>
                {/* Involved / Witness Selection */}
                <Typography sx={{ fontSize: "14px", mb: 1, color: "black" }}>Involved / Witness</Typography>
                <FormControl>
                  <RadioGroup
                    row
                    value={role}
                    onChange={(e) => handleRoleChange(e, e.target.value)}
                    sx={{ mb: 3, gap: 2 }}
                  >
                    <FormControlLabel
                      value='Involved'
                      control={
                        <Radio
                          sx={{
                            "&.Mui-checked": {
                              color: "#1751D0",
                            },
                          }}
                        />
                      }
                      label='Involved'
                      sx={{
                        border: "1px solid #E0E0E0",
                        borderRadius: 1,
                        flex: 1,
                        mr: 0,
                        ml: 0,
                        pl: 1,
                        width: "150px",
                        "&:hover": {
                          backgroundColor: "#F5F5F5",
                        },
                      }}
                    />
                    <FormControlLabel
                      value='Witness'
                      control={
                        <Radio
                          sx={{
                            "&.Mui-checked": {
                              color: "#1751D0",
                            },
                          }}
                        />
                      }
                      label='Witness'
                      sx={{
                        border: "1px solid #E0E0E0",
                        borderRadius: 1,
                        flex: 1,
                        mr: 0,
                        pl: 1,
                        width: "150px",
                        "&:hover": {
                          backgroundColor: "#F5F5F5",
                        },
                      }}
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>
            </Grid>
            <Grid container>
              <Grid item lg={8}>
                {/* Adult / Child Selection */}
                <Typography sx={{ fontSize: "14px", mb: 1, color: "black" }}>Adult / Child</Typography>
                <FormControl>
                  <RadioGroup
                    row
                    value={category}
                    onChange={(e) => handleCategoryChange(e, e.target.value)}
                    sx={{ mb: 3, gap: 2 }}
                  >
                    <FormControlLabel
                      value='Adult'
                      control={
                        <Radio
                          sx={{
                            "&.Mui-checked": {
                              color: "#1751D0",
                            },
                          }}
                        />
                      }
                      label='Adult'
                      sx={{
                        border: "1px solid #E0E0E0",
                        borderRadius: 1,
                        flex: 1,
                        ml: 0,
                        mr: 0,
                        pl: 1,
                        width: "150px",
                        "&:hover": {
                          backgroundColor: "#F5F5F5",
                        },
                      }}
                    />
                    <FormControlLabel
                      value='Child'
                      control={
                        <Radio
                          sx={{
                            "&.Mui-checked": {
                              color: "#1751D0",
                            },
                          }}
                        />
                      }
                      label='Child'
                      sx={{
                        border: "1px solid #E0E0E0",
                        borderRadius: 1,
                        flex: 1,
                        mr: 0,
                        pl: 1,
                        width: "150px",
                        "&:hover": {
                          backgroundColor: "#F5F5F5",
                        },
                      }}
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>
            </Grid>
          </ReusableDialog>
        </Grid>
      </Grid>

      {/* Footer Actions */}
      <Box
        sx={{
          position: "fixed",
          bottom: 20,
          right: 20,
          zIndex: 1000,
          display: "flex",
          gap: 2,
        }}
      >
        <Button
          variant='outlined'
          color='primary'
          onClick={() => {
            window.scrollTo(0, 0); // Prevent scrolling
            prevStep();
          }}
        >
          Previous
        </Button>

        <Button
          variant='contained'
          color='primary'
          onClick={() => {
            window.scrollTo(0, 0); // Prevent scrolling
            nextStep(); // Call the function properly
            updateFormData({ nonPhysicalWitnessInfo: witnessData }); // Call the function properly
          }}
          disabled={witnessData.length === 0}
        >
          Next
        </Button>
      </Box>
    </Box>
  );
};

export default NonPhysicalWitness;
